<template>
  <ValidationProvider
    v-slot="{ errors }"
    :vid="vid"
    :name="name"
    :rules="rules"
    tag="div"
    ref="provider"
  >
    <AGCLabel v-if="label" :required="required" :id="id">
      <slot>{{ label }}</slot>
      <template v-slot:subLabel>
        <slot name="subLabel">{{ subLabel }}</slot>
      </template>
    </AGCLabel>
    <div>
      <vue-dropzone
        v-if="!readOnly"
        :id="dropzoneId"
        :options="dropzoneOptions"
        :useCustomSlot="true"
        ref="dropzone"
        @vdropzone-files-added="handleAdded"
        @vdropzone-success="onFileSuccess"
        @vdropzone-success-multiple="onMultipleFileSuccess"
        @vdropzone-error="handleError"
        @vdropzone-complete="handleComplete"
        @vdropzone-complete-multiple="handleCompleteMultiple"
      >
        <div class="dropzone-custom-content">
          <h6 v-if="dropzoneTitle">{{ dropzoneTitle }}</h6>
          <img
            src="../../assets/img/icon/cloud-upload.png"
            alt="Cloud upload Icon"
            class="img-upload"
          />
          <h3 class="dropzone-custom-title" v-show="!inputVal || !inputVal.length">
            拖放檔案到這裡或
          </h3>
          <div class="subtitle">
            <button class="btn-upload-image">選取檔案</button>
          </div>
        </div>
      </vue-dropzone>
      <div class="file-uploader__error-list">
        <span v-if="errors" class="file-uploader__error">{{ errors[0] }}</span>
        <span v-for="name in Object.keys(customErrors)"
              :key="name"
              class="file-uploader__error"
        >
          {{ name }} - {{ customErrors[name] }}
        </span>
      </div>
      <div class="dropzone__hint">
        <slot name="hint"></slot>
      </div>
      <ThumbnailPreviewList
        v-model="inputVal"
        v-stream:remove-click="onRemoveClick$"
        :multiple="multiple"
        :upload-type="uploadType"
        :readOnly="readOnly"
        :t="t"
        :role="role"
        :dropzoneTitle="dropzoneTitle"
        @edit="handleEdit"
        @download="downloadFile"
      ></ThumbnailPreviewList>
    </div>
    <CropperModal
      v-model="cropperModal"
      :upload-type="uploadType"
      :public-api="publicApi"
      @cropped="reloadImages"
    ></CropperModal>
  </ValidationProvider>
</template>

<script>
import { tap, pluck } from 'rxjs/operators';
import { mapGetters } from 'vuex';
import vue2Dropzone from 'vue2-dropzone';
import store, { REQUIRE_LOGIN } from '@/store';
import imagePath from '@/filters/image-path';

import ThumbnailPreviewList from '@/components/thumbnail/ThumbnailPreviewList.vue';
import CropperModal from '@/components/image/CropperModal.vue';
import agcFormElementMixin from '../../mixins/agc-form-element';

export default {
  mixins: [agcFormElementMixin],
  components: {
    vueDropzone: vue2Dropzone,
    ThumbnailPreviewList,
    CropperModal,
  },
  props: {
    maxFiles: {
      type: Number,
      default: 1,
    },
    publicApi: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    uploadType: String,
    acceptedFiles: {
      type: String,
      default: '.jpg,.jpeg,.png,.gif',
    },
    filters: {
      imagePath,
    },
    dropzoneTitle: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    cropperModal: {
      show: false,
      image: null,
    },
    t: null,
    customErrors: {},
  }),
  domStreams: ['onRemoveClick$'],
  subscriptions() {
    return {
      onRemoveClick: this.onRemoveClick$.pipe(
        pluck('event', 'msg'),
        tap((imageNameToRemove) => {
          if (this.multiple) {
            this.inputVal = this.inputVal.filter((imageName) => imageName !== imageNameToRemove);
          } else {
            this.inputVal = null;
          }
        }),
      ),
    };
  },
  computed: {
    ...mapGetters(['role']),
    multiple() {
      return this.maxFiles > 1;
    },
    apiBase() {
      return this.publicApi ? 'public' : this.role;
    },
    uploadUrl() {
      const uri = this.multiple ? 'images' : 'image';
      return `/agc-${this.apiBase}-api/upload/${uri}?type=${this.uploadType}`;
    },
    dropzoneId() {
      return `df-${this.id}`;
    },
    dropzoneOptions() {
      return {
        uploadMultiple: this.multiple,
        url: this.uploadUrl,
        paramName: 'upload',
        parallelUploads: this.maxFiles,
        thumbnailWidth: 150,
        thumbnailHeight: 150,
        thumbnailMethod: 'contain',
        maxFiles: this.maxFiles,
        maxFilesize: 2,
        acceptedFiles: this.acceptedFiles,
        dictFileTooBig: '檔案大小不能多於{{maxFilesize}}MB',
        dictInvalidFileType: '請上傳正確檔案類型',
        dictCancelUpload: '取消上傳',
        dictCancelUploadConfirmation: '確定移除圖片嗎？',
        addRemoveLinks: true,
        dictRemoveFile: '刪除',
      };
    },
  },
  methods: {
    handleAdded() {
      this.customErrors = {};
    },
    onFileSuccess(_, { payload }) {
      if (!this.multiple) {
        this.inputVal = payload;
      }
    },
    onMultipleFileSuccess(_, { payload }) {
      this.inputVal = this.inputVal.concat(payload);
      const exceed = this.inputVal.length - this.maxFiles;
      if (exceed > 0) {
        this.inputVal.splice(0, exceed);
      }
    },
    handleEdit(image) {
      this.cropperModal = {
        show: true,
        image,
      };
    },
    downloadFile(image) {
      const url = imagePath(image, this.uploadType, 'original', true, this.t);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', image);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    reloadImages() {
      this.t = new Date().getTime();
    },
    handleError({ name }, msg) {
      if (msg.msg === 'UNAUTHORIZED') {
        store.dispatch(REQUIRE_LOGIN);
        return;
      }
      this.$nextTick(() => {
        this.$set(this.customErrors, name, msg);
      });
    },
    handleComplete() {
      if (!this.multiple) {
        this.$refs.dropzone.removeAllFiles();
      }
    },
    handleCompleteMultiple() {
      this.$refs.dropzone.removeAllFiles();
    },
  },
};
</script>

<style>
@import '~vue2-dropzone/dist/vue2Dropzone.min.css';
</style>

<style scoped lang="scss">
.file-uploader {
  &__error-list {
    display: flex;
    flex-direction: column;
  }

  &__error {
    font-size: 80%;
    color: #dc3545;
  }
}

.dropzone {
  font-family: inherit;
  background: #f9f9fa;
  text-align: center;
  vertical-align: middle;
  padding: 15px;
  min-height: unset;
  border: 2px dashed #CED4D9;
  border-radius: 4px;
  background-color: #FFFFFF;

  &-custom-title {
    font-size: 14px;
    font-weight: 400;
    padding: 6px 0px;
    color: #aaa;
  }

  .img-upload {
    width: 50px;
    margin-top: 16px;
    margin-bottom: 4px;
  }

  .subtitle {
    .btn-upload-image {
      background-color: #e0effd;
      border: 1px solid #b1d7fb;
      font-size: 14px;
      color: #318ce1;
      border-radius: 4px;
      padding: 6px 16px;
      margin-bottom: 16px;
    }
  }

  &__hint {
    margin-top: 4px;
    color: #7e8b97;
    font-size: 12px;
  }

  ::v-deep {
    .dz-message {
      margin: 0;
    }

    .dz-preview {
      .dz-error-message {
        top: 50%;
      }
      .dz-error-mark {
        top: 22%;
        display: none;
      }
      .dz-image {
        img {
          height: 9rem;
          width: 9rem;
          object-fit: cover;
        }
      }
      &.dz-error.dz-complete {
         .dz-remove {
           left: 32%;
         }
      }
      .dz-remove {
        margin: 0px;
        top: 15%;
        bottom: initial;
        left: 22%;

        &:hover {
          text-decoration: none;
        }
      }
    }

    .dz-progress {
      top: 80%;
    }

    .dz-details {
      .dz-size,
      .dz-filename {
        display: none;
      }
    }
  }
}
</style>
